/**
 * Frontend recurrence pattern utilities for client-side expansion and display
 */

import { CalendarEvent, RecurringPattern } from '../types/calendar';

/**
 * Parse a recurring pattern from string or object format
 */
export function parseRecurringPattern(pattern: RecurringPattern | string | undefined): RecurringPattern | null {
  if (!pattern) {
    return null;
  }

  // If already a structured object, return it
  if (typeof pattern === 'object' && pattern.frequency) {
    return pattern;
  }

  // Legacy string format parsing
  if (typeof pattern === 'string') {
    const normalized = pattern.toLowerCase().trim();
    
    if (normalized === 'daily' || normalized === 'day') {
      return { frequency: 'daily', interval: 1 };
    }
    if (normalized === 'weekly' || normalized === 'week') {
      return { frequency: 'weekly', interval: 1 };
    }
    if (normalized === 'monthly' || normalized === 'month') {
      return { frequency: 'monthly', interval: 1 };
    }
    if (normalized === 'yearly' || normalized === 'year' || normalized === 'annually') {
      return { frequency: 'yearly', interval: 1 };
    }

    // Try to parse JSON format
    try {
      const parsed = JSON.parse(pattern);
      if (parsed.frequency) {
        return parsed;
      }
    } catch {
      // Not JSON, continue with default
    }

    return { frequency: 'weekly', interval: 1 };
  }

  return null;
}

/**
 * Expand a recurring event into multiple instances within a date range (client-side)
 */
export function expandRecurringEvent(
  event: CalendarEvent,
  startDate: Date,
  endDate: Date
): CalendarEvent[] {
  if (!event.isRecurring) {
    return [event];
  }

  const pattern = parseRecurringPattern(event.recurringPattern);
  if (!pattern) {
    return [event];
  }

  const instances: CalendarEvent[] = [];
  const rangeStart = new Date(startDate);
  const rangeEnd = new Date(endDate);
  rangeEnd.setHours(23, 59, 59, 999);
  rangeStart.setHours(0, 0, 0, 0);

  const originalStart = new Date(event.startTime);
  const originalEnd = new Date(event.endTime);
  const durationMs = originalEnd.getTime() - originalStart.getTime();

  // Start from the original event date, but fast-forward to first occurrence in range if needed
  let currentDate = new Date(originalStart);
  let occurrenceCount = 0;
  const maxOccurrences = 1000;

  // If the original event is before the range, fast-forward to the first occurrence in range
  // But be careful: we want to include the first occurrence that overlaps the range
  if (currentDate < rangeStart) {
    // Fast-forward until we reach or pass the range start
    // We'll check overlap in the main loop, so we can go slightly before rangeStart
    let iterations = 0;
    const maxFastForward = 10000; // Safety limit for fast-forward
    
    while (currentDate < rangeStart && iterations < maxFastForward) {
      const nextDate = calculateNextOccurrence(currentDate, pattern);
      if (nextDate.getTime() === currentDate.getTime()) {
        // No progress, break to avoid infinite loop
        break;
      }
      
      // If next occurrence is still before rangeStart, continue
      // If next occurrence is at or after rangeStart, we'll check it in the main loop
      if (nextDate >= rangeStart) {
        currentDate = nextDate;
        break;
      }
      
      currentDate = nextDate;
      iterations++;
      
      // Safety check: if we've gone way past, something is wrong
      if (currentDate > rangeEnd && currentDate.getTime() - rangeEnd.getTime() > 365 * 24 * 60 * 60 * 1000) {
        // More than a year past the range, something is wrong
        break;
      }
    }
    // Reset occurrence count for actual instances we'll create
    occurrenceCount = 0;
  }

  // Now generate instances within the range
  while (currentDate <= rangeEnd && occurrenceCount < maxOccurrences) {
    // Check if this occurrence overlaps with the requested range
    const instanceEnd = new Date(currentDate.getTime() + durationMs);
    
    // Overlaps if: instanceStart <= rangeEnd AND instanceEnd >= rangeStart
    if (currentDate <= rangeEnd && instanceEnd >= rangeStart) {
      if (pattern.endDate) {
        const patternEndDate = new Date(pattern.endDate);
        if (currentDate > patternEndDate) {
          break;
        }
      }

      if (pattern.count && occurrenceCount >= pattern.count) {
        break;
      }

      const instance: CalendarEvent = {
        ...event,
        id: `${event.id}_${occurrenceCount}`,
        startTime: currentDate,
        endTime: instanceEnd,
      };

      instances.push(instance);
      occurrenceCount++;
    }

    const nextDate = calculateNextOccurrence(currentDate, pattern);
    if (nextDate.getTime() === currentDate.getTime()) {
      break;
    }
    currentDate = nextDate;

    // If we've gone past the range, we can stop
    if (currentDate > rangeEnd) {
      break;
    }
  }

  if (instances.length === 0 && originalStart <= rangeEnd && originalEnd >= rangeStart) {
    return [event];
  }

  return instances;
}

/**
 * Calculate the next occurrence date based on the recurring pattern
 */
function calculateNextOccurrence(currentDate: Date, pattern: RecurringPattern): Date {
  const next = new Date(currentDate);

  switch (pattern.frequency) {
    case 'daily':
      next.setDate(next.getDate() + pattern.interval);
      break;
    
    case 'weekly':
      next.setDate(next.getDate() + (7 * pattern.interval));
      break;
    
    case 'monthly':
      next.setMonth(next.getMonth() + pattern.interval);
      if (next.getDate() !== currentDate.getDate()) {
        next.setDate(0);
        next.setMonth(next.getMonth() + 1);
        next.setDate(Math.min(currentDate.getDate(), getDaysInMonth(next)));
      }
      break;
    
    case 'yearly':
      next.setFullYear(next.getFullYear() + pattern.interval);
      if (currentDate.getMonth() === 1 && currentDate.getDate() === 29) {
        if (!isLeapYear(next.getFullYear())) {
          next.setMonth(1);
          next.setDate(28);
        }
      }
      break;
  }

  return next;
}

/**
 * Get number of days in a month
 */
function getDaysInMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

/**
 * Check if a year is a leap year
 */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

/**
 * Format recurring pattern for display
 */
export function formatRecurringPattern(pattern: RecurringPattern | string | undefined): string {
  const parsed = parseRecurringPattern(pattern);
  if (!parsed) {
    return '';
  }

  const { frequency, interval } = parsed;

  if (interval === 1) {
    switch (frequency) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return 'Weekly';
      case 'monthly':
        return 'Monthly';
      case 'yearly':
        return 'Yearly';
    }
  } else {
    switch (frequency) {
      case 'daily':
        return `Every ${interval} days`;
      case 'weekly':
        return `Every ${interval} weeks`;
      case 'monthly':
        return `Every ${interval} months`;
      case 'yearly':
        return `Every ${interval} years`;
    }
  }

  return '';
}

/**
 * Calculate next occurrence date for display
 */
export function getNextOccurrence(event: CalendarEvent): Date | null {
  if (!event.isRecurring) {
    return null;
  }

  const pattern = parseRecurringPattern(event.recurringPattern);
  if (!pattern) {
    return null;
  }

  const startDate = new Date(event.startTime);
  return calculateNextOccurrence(startDate, pattern);
}
